// src/hooks/useCustomers.js
import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './useAuth';

// Hook for admin to get all customers
export function useCustomers() {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading || !user || !isAdmin) {
      if (!authLoading) setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, 'customers'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const customerList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCustomers(customerList);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching customers:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading, isAdmin]);

  return { customers, loading, error };
}

// Hook for rep to get their own customers
export function useRepCustomers(repId) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!repId) {
      setCustomers([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'customers'),
      where('repId', '==', repId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const customerList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCustomers(customerList);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching rep customers:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [repId]);

  return { customers, loading, error };
}

// Hook to get customers by rep for admin view
export function useCustomersByRep() {
  const { customers, loading, error } = useCustomers();
  
  const customersByRep = customers.reduce((acc, customer) => {
    const repId = customer.repId || 'direct';
    if (!acc[repId]) {
      acc[repId] = [];
    }
    acc[repId].push(customer);
    return acc;
  }, {});

  return { customersByRep, customers, loading, error };
}
